import React from "react";

const STEPS = [
  {
    title: "Ramsay signs up",
    caption: "Creates a vendor login with the service account address",
  },
  {
    title: "Vendor emails the account",
    caption: "2FA codes, magic links, password resets, verification",
  },
  {
    title: "Polaris receives it",
    caption: "Postmark delivers to the Service Account Inbox",
  },
  {
    title: "Message is classified",
    caption: "Matched to a vendor; code or link extracted",
  },
  {
    title: "Ramsay finishes login",
    caption: "Picks up the result over MCP and continues",
  },
];

const NODE_W = 160;
const NODE_H = 116;
const GAP = 34;
const TOP = 74;
const WIDTH = STEPS.length * NODE_W + (STEPS.length - 1) * GAP + 2;
const HEIGHT = TOP + NODE_H + 24;

const SparklesGlyph = ({ x, y }: { x: number; y: number }) => (
  <g transform={`translate(${x} ${y})`} fill="none" stroke="currentColor" strokeWidth={1.6}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.5 3.5 10.8 7.2 14.5 8.5 10.8 9.8 9.5 13.5 8.2 9.8 4.5 8.5 8.2 7.2Z M17 12l.8 2.2 2.2.8-2.2.8L17 18l-.8-2.2-2.2-.8 2.2-.8Z M4 14.5l.6 1.4 1.4.6-1.4.6L4 18.5l-.6-1.4-1.4-.6 1.4-.6Z"
    />
  </g>
);

const EnvelopeGlyph = ({ x, y }: { x: number; y: number }) => (
  <g transform={`translate(${x} ${y})`} fill="none" stroke="currentColor" strokeWidth={1.6}>
    <rect x={2.5} y={5} width={19} height={14} rx={2.5} />
    <path strokeLinecap="round" strokeLinejoin="round" d="m3.5 7 8.5 6 8.5-6" />
  </g>
);

const InboxGlyph = ({ x, y }: { x: number; y: number }) => (
  <g transform={`translate(${x} ${y})`} fill="none" stroke="currentColor" strokeWidth={1.6}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 13.5V17a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3.5M3 13.5l2.6-7.2A2 2 0 0 1 7.5 5h9a2 2 0 0 1 1.9 1.3L21 13.5M3 13.5h4.5l1.5 2.5h6l1.5-2.5H21"
    />
  </g>
);

const TagGlyph = ({ x, y }: { x: number; y: number }) => (
  <g transform={`translate(${x} ${y})`} fill="none" stroke="currentColor" strokeWidth={1.6}>
    <rect x={3} y={4} width={18} height={16} rx={2.5} />
    <path strokeLinecap="round" d="M7 9h10M7 12.5h7M7 16h4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="m15.5 14.5 1.5 1.5 3-3" />
  </g>
);

const KeyGlyph = ({ x, y }: { x: number; y: number }) => (
  <g transform={`translate(${x} ${y})`} fill="none" stroke="currentColor" strokeWidth={1.6}>
    <circle cx={8} cy={9} r={4.5} />
    <path strokeLinecap="round" strokeLinejoin="round" d="m11.5 12 8 8M16.5 17l2-2M18.5 19l2-2" />
  </g>
);

const GLYPHS = [SparklesGlyph, EnvelopeGlyph, InboxGlyph, TagGlyph, KeyGlyph];

/**
 * Explains the service account round trip: Ramsay signs up for a vendor with the organization's address, the
 * vendor's email lands in Polaris, is matched to the vendor and has its code or link extracted, and Ramsay
 * picks it up to finish logging in.
 * Pure inline SVG so it inherits the page's colors and scales with its container.
 */
export const ServiceAccountFlowDiagram = ({ emailExample }: { emailExample: string }) => (
  <svg
    viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
    role="img"
    aria-label="How service accounts work: Ramsay signs up with a service account address, the vendor emails it, Polaris receives and classifies the message, and Ramsay finishes logging in."
    className="w-full h-auto text-indigo-700"
  >
    <defs>
      <marker
        id="sa-arrow"
        viewBox="0 0 10 10"
        refX="9"
        refY="5"
        markerWidth="7"
        markerHeight="7"
        orient="auto"
      >
        <path d="M0 0 10 5 0 10z" fill="#6366f1" />
      </marker>
    </defs>

    {/* The address that ties the whole loop together */}
    <g>
      <rect
        x={WIDTH / 2 - 190}
        y={12}
        width={380}
        height={36}
        rx={18}
        fill="#eef2ff"
        stroke="#c7d2fe"
      />
      <text
        x={WIDTH / 2}
        y={35}
        textAnchor="middle"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
        fontSize={14}
        fill="#3730a3"
      >
        {emailExample}
      </text>
      {/* Dashed ties from the address down to the two steps that use it */}
      <path
        d={`M${WIDTH / 2 - 150} 48 C ${WIDTH / 2 - 150} 62, ${NODE_W / 2} 56, ${NODE_W / 2} ${TOP}`}
        fill="none"
        stroke="#a5b4fc"
        strokeWidth={1.5}
        strokeDasharray="4 4"
      />
      <path
        d={`M${WIDTH / 2 + 150} 48 C ${WIDTH / 2 + 150} 62, ${NODE_W * 1.5 + GAP} 56, ${
          NODE_W * 1.5 + GAP
        } ${TOP}`}
        fill="none"
        stroke="#a5b4fc"
        strokeWidth={1.5}
        strokeDasharray="4 4"
      />
    </g>

    {STEPS.map((step, i) => {
      const x = 1 + i * (NODE_W + GAP);
      const Glyph = GLYPHS[i];
      const isRamsay = i === 0 || i === STEPS.length - 1;
      return (
        <g key={step.title}>
          <rect
            x={x}
            y={TOP}
            width={NODE_W}
            height={NODE_H}
            rx={12}
            fill={isRamsay ? "#eef2ff" : "#ffffff"}
            stroke={isRamsay ? "#818cf8" : "#d4d4d8"}
            strokeWidth={1.5}
          />
          <circle cx={x + NODE_W / 2} cy={TOP + 26} r={16} fill="#ffffff" stroke="#c7d2fe" />
          <Glyph x={x + NODE_W / 2 - 12} y={TOP + 14} />
          <text
            x={x + NODE_W / 2}
            y={TOP + 62}
            textAnchor="middle"
            fontSize={13}
            fontWeight={600}
            fill="#18181b"
          >
            {step.title}
          </text>
          <foreignObject x={x + 10} y={TOP + 70} width={NODE_W - 20} height={NODE_H - 76}>
            <div
              style={{
                fontSize: "11px",
                lineHeight: "14px",
                color: "#52525b",
                textAlign: "center",
              }}
            >
              {step.caption}
            </div>
          </foreignObject>
          <text
            x={x + 14}
            y={TOP + NODE_H - 8}
            fontSize={10}
            fontWeight={600}
            fill="#a1a1aa"
            letterSpacing={0.5}
          >
            {`0${i + 1}`}
          </text>
          {i < STEPS.length - 1 && (
            <line
              x1={x + NODE_W + 4}
              y1={TOP + NODE_H / 2}
              x2={x + NODE_W + GAP - 4}
              y2={TOP + NODE_H / 2}
              stroke="#6366f1"
              strokeWidth={2}
              markerEnd="url(#sa-arrow)"
            />
          )}
        </g>
      );
    })}
  </svg>
);
